import React from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Stack,
} from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { ServiceDetailedMetrics } from '../entity';

type ServiceDetailedMetricsTableProps = {
  services: ServiceDetailedMetrics[];
  formatCurrency: (amount: number) => string;
  formatPercentage: (value: number) => string;
};

/**
 * Tabulka s detailními metrikami pro každou službu
 */
export const ServiceDetailedMetricsTable: React.FC<ServiceDetailedMetricsTableProps> = ({
  services,
  formatCurrency,
  formatPercentage,
}) => {
  if (services.length === 0) {
    return null;
  }

  // Seřadit podle profit per hour (sestupně)
  const sortedServices = [...services].sort((a, b) => b.profitPerHour - a.profitPerHour);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        <FormattedMessage
          id="pricing.metrics.serviceDetails"
          defaultMessage="Detailní analýza služeb"
        />
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        <FormattedMessage
          id="pricing.metrics.serviceDetails.description"
          defaultMessage="Kompletní přehled všech metrik pro každou službu"
        />
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} sx={{ verticalAlign: 'bottom' }}>
                <FormattedMessage id="pricing.metrics.table.service" defaultMessage="Služba" />
              </TableCell>
              <TableCell align="right" rowSpan={2} sx={{ verticalAlign: 'bottom' }}>
                <FormattedMessage id="pricing.metrics.table.currentPrice" defaultMessage="Cena" />
              </TableCell>
              <TableCell align="right" rowSpan={2} sx={{ verticalAlign: 'bottom' }}>
                <FormattedMessage
                  id="pricing.metrics.table.variableCost"
                  defaultMessage="Var. náklady"
                />
              </TableCell>
              <TableCell align="right" rowSpan={2} sx={{ verticalAlign: 'bottom' }}>
                <FormattedMessage id="pricing.metrics.table.contribution" defaultMessage="Marže" />
              </TableCell>
              <TableCell align="right" rowSpan={2} sx={{ verticalAlign: 'bottom' }}>
                <FormattedMessage
                  id="pricing.metrics.table.profitPerHour"
                  defaultMessage="Zisk/h"
                />
              </TableCell>
              <TableCell align="center" colSpan={2}>
                <FormattedMessage
                  id="pricing.metrics.table.priceIncrease"
                  defaultMessage="Řešení B-E"
                />
              </TableCell>
              <TableCell align="right" rowSpan={2} sx={{ verticalAlign: 'bottom' }}>
                <FormattedMessage id="pricing.metrics.table.performed" defaultMessage="Provedeno" />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right">
                <FormattedMessage
                  id="pricing.metrics.table.priceIncreaseAmount"
                  defaultMessage="Zdražení"
                />
              </TableCell>
              <TableCell align="right">
                <FormattedMessage
                  id="pricing.metrics.table.additionalVisits"
                  defaultMessage="Návštěvy navíc"
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedServices.map((service, index) => {
              const isTop3 = index < 3;
              return (
                <TableRow
                  key={service.serviceId}
                  sx={{
                    backgroundColor: isTop3 ? 'success.light' : 'transparent',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  {/* Název služby */}
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {isTop3 && <Chip label={`#${index + 1}`} size="small" color="success" />}
                      <Typography variant="body2">{service.serviceName}</Typography>
                    </Stack>
                  </TableCell>

                  {/* Současná cena */}
                  <TableCell align="right">
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(service.currentPrice)}
                      </Typography>
                      <Typography
                        variant="caption"
                        fontSize="0.7rem"
                        sx={{
                          color:
                            service.averageActualPrice < service.individual.breakEvenPriceIndividual
                              ? 'error.main'
                              : 'text.secondary',
                        }}
                      >
                        ø {formatCurrency(service.averageActualPrice)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Variabilní náklady */}
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(service.averageVariableCost)}
                    </Typography>
                  </TableCell>

                  {/* Contribution marže */}
                  <TableCell align="right">
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(service.contributionMargin)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({formatPercentage(service.contributionMarginPercentage)})
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Profit per hour */}
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      {service.profitPerHour > 0 ? (
                        <TrendingUp color="success" fontSize="small" />
                      ) : (
                        <TrendingDown color="error" fontSize="small" />
                      )}
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={service.profitPerHour > 0 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(service.profitPerHour)}
                      </Typography>
                    </Stack>
                  </TableCell>

                  {/* Zdražení - Individuální */}
                  <TableCell align="right">
                    {service.individual.priceIncreaseIndividual > 0 ? (
                      <Box>
                        <Typography variant="body2" color="warning.main" fontWeight="bold">
                          {formatCurrency(service.individual.breakEvenPriceIndividual)}
                        </Typography>
                        <Typography variant="body2" color="warning.main">
                          +{formatCurrency(service.individual.priceIncreaseIndividual)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({formatPercentage(service.individual.priceIncreaseIndividualPercentage)})
                        </Typography>
                      </Box>
                    ) : (
                      <Chip label="✓" size="small" color="success" />
                    )}
                  </TableCell>

                  {/* Návštěvy navíc - Individuální */}
                  <TableCell align="right">
                    {service.individual.additionalVisitsIndividualNeeded > 0 ? (
                      <Box>
                        <Chip
                          label={`+${service.individual.additionalVisitsIndividualNeeded}`}
                          size="small"
                          color="warning"
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          (→ {service.individual.breakEvenTotalVisitsIndividual})
                        </Typography>
                      </Box>
                    ) : (
                      <Chip label="✓" size="small" color="success" />
                    )}
                  </TableCell>

                  {/* Počet provedení */}
                  <TableCell align="right">
                    <Typography variant="body2">{service.currentTimesPerformed}×</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {service.durationMinutes} min
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legenda */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
          <FormattedMessage id="pricing.metrics.legend" defaultMessage="Legenda:" />
        </Typography>
        <Stack spacing={0.5}>
          <Typography variant="caption">
            <strong>
              <FormattedMessage id="pricing.metrics.legend.price" defaultMessage="Cena:" />
            </strong>{' '}
            <FormattedMessage
              id="pricing.metrics.legend.price.desc"
              defaultMessage="Ceníková cena (tučně) a průměrná skutečně zaplacená cena ø (zohledňuje slevy, věrnostní body)"
            />
          </Typography>
          <Typography variant="caption">
            <strong>
              <FormattedMessage id="pricing.metrics.legend.contribution" defaultMessage="Marže:" />
            </strong>{' '}
            <FormattedMessage
              id="pricing.metrics.legend.contribution.desc"
              defaultMessage="Cena - Náklady na materiál (příspěvek na pokrytí fixních nákladů)"
            />
          </Typography>
          <Typography variant="caption">
            <strong>
              <FormattedMessage
                id="pricing.metrics.legend.profitPerHour"
                defaultMessage="Zisk/h:"
              />
            </strong>{' '}
            <FormattedMessage
              id="pricing.metrics.legend.profitPerHour.desc"
              defaultMessage="Marže / (trvání služby v hodinách)"
            />
          </Typography>
          <Typography variant="caption">
            <strong>
              <FormattedMessage
                id="pricing.metrics.legend.individual"
                defaultMessage="Řešení B-E:"
              />
            </strong>{' '}
            <FormattedMessage
              id="pricing.metrics.legend.individual.desc"
              defaultMessage="Každá služba Vám zabere čas a náklady na ni jsou různé. Zde je uvedeno o kolik je potřeba každou službu zdražit nebo kolik návštěv navíc je potřeba k dosažení break-even bodu při stejné ceně, bodu kdy zvládnete ušetřit na požadované stálé náklady."
            />
          </Typography>
          <Typography variant="caption">
            <strong>
              <FormattedMessage
                id="pricing.metrics.legend.additionalVisits"
                defaultMessage="Návštěvy navíc:"
              />
            </strong>{' '}
            <FormattedMessage
              id="pricing.metrics.legend.additionalVisits.desc"
              defaultMessage="Alternativa ke zdražení - kolik návštěv navíc je potřeba při současné ceně"
            />
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
};
